import base64Helper from '../base64-helper';

const { utf8ArrayToStr } = base64Helper;

let _ticketCounter = 0;
const _promiseMap = {};

const VALID_DEBUG_URL =
    /^http[s]?:\/\/[a-z.]*(evbdev.com||evbqa.com||localhost):[0-9]+/;

const isExternal = (url) => {
    if (url && url.match(/mock-data\/|images\//)) {
        return false;
    }
    return true;
};

const adjustAjaxToIframeProxy = () => {
    const oldFetch = window.fetch;

    window.fetch = (url, options) => {
        if (isExternal(url)) {
            const promise = new Promise((resolve, reject) => {
                const ticket = _ticketCounter++;

                _promiseMap[ticket] = {
                    resolve,
                    reject,
                };

                window.parent.postMessage(
                    {
                        iProxyRequest: true,
                        url,
                        options,
                        ticket,
                    },
                    '*',
                );
            });

            return promise;
        }
        return oldFetch(url, options);
    };

    const getPayload = (payloadOrBuffer) => {
        if (typeof payloadOrBuffer === 'string') {
            return payloadOrBuffer;
        }
        const payload = utf8ArrayToStr(new Uint8Array(payloadOrBuffer));

        return payload;
    };

    window.addEventListener('message', (e) => {
        const data = e.data;

        if (data && data.iProxyResponse) {
            const { ticket, payload: payloadOrBuffer, headers = {} } = data;
            const record = _promiseMap[ticket];

            if (record) {
                delete _promiseMap[ticket];
                record.resolve({
                    headers,
                    json: () => {
                        const payload = getPayload(payloadOrBuffer);
                        const jsonData = JSON.parse(payload);

                        return Promise.resolve(jsonData);
                    },
                    text: () => {
                        const payload = getPayload(payloadOrBuffer);

                        Promise.resolve(payload);
                    },
                    arrayBuffer: () => Promise.resolve(payloadOrBuffer),
                });
            }
        }
    });
};

const installIframeProxy = () => {
    let debugUrl;
    let extraQueryStr;
    try {
        debugUrl = localStorage.debugUrl;
        extraQueryStr = localStorage.extraQuery;
    } catch (error) {
        // no access to local storage, exit silently
        return;
    }

    if (!debugUrl) {
        console.error(
            'Iframe proxy. Please set localStorage.debugUrl to a proper url to proxy',
        );
        return;
    }
    if (!debugUrl.match(VALID_DEBUG_URL)) {
        console.error('Invalid debugUrl');
        return;
    }

    let query = {
        insideIFrameProxy: 'true',
    };

    if (extraQueryStr) {
        query = {
            ...query,
            ...JSON.parse(extraQueryStr),
        };
    }
    const iframe = document.createElement('iframe');

    console.log('Iframe src: ', window.location.href);
    const search = window.location.search;

    const queryKeys = Object.keys(query);

    const fullSearch = queryKeys.reduce((str, key) => {
        const prefix = str ? '&' : '?';

        return `${str}${prefix}${key}=${query[key]}`;
    }, search);

    const debugSrc = `${debugUrl}${window.location.pathname}${fullSearch}${window.location.hash}`;

    console.log(`Setting debugUrl to be: ${debugSrc}`);
    iframe.style = 'width:100vw; height:100vh';
    iframe.frameBorder = 0;
    iframe.src = debugSrc;

    document.body.appendChild(iframe);

    window.receivedMessages = [];

    window.onmessage = (e) => {
        if (e.data && e.data.iProxyRequest) {
            const { ticket, url, internalRequest, options = {} } = e.data;

            if (url) {
                fetch(url, options)
                    .then((result) => {
                        const headers = {};

                        result.headers.forEach((value, key) => {
                            headers[key] = value;
                        });
                        return result.arrayBuffer().then((payload) => ({
                            payload,
                            headers,
                        }));
                    })
                    .then(({ payload, headers }) => {
                        iframe.contentWindow.postMessage(
                            {
                                iProxyResponse: true,
                                ticket,
                                payload,
                                headers,
                            },
                            '*',
                        );
                    });
            } else if (internalRequest) {
                switch (internalRequest) {
                    case 'embeddedInfo':
                        // Please turn this on if debugging is needed
                        //console.log('Returning embedded info request to child iframe');
                        iframe.contentWindow.postMessage(
                            {
                                iProxyResponse: true,
                                ticket,
                                payload: JSON.stringify({
                                    serverData: window.__SERVER_DATA__ || {},
                                    cookie: document.cookie,
                                }),
                            },
                            '*',
                        );
                        break;
                    default:
                }
            }
        } else if (e.source === window.parent) {
            iframe.contentWindow.postMessage(e.data, '*');
        } else if (window.parent) {
            window.parent.postMessage(e.data, '*');
        }
    };
};

const isIframeProxy = () => {
    try {
        if (localStorage && localStorage.debugUrl) {
            return true;
        }
    } catch (error) {
        // No access to local storage, do nothing.
    }

    return false;
};

const aGetEmbeddedInfoFromIframeProxy = async () => {
    const promise = new Promise((resolve, reject) => {
        const ticket = _ticketCounter++;

        _promiseMap[ticket] = {
            resolve,
            reject,
        };
        //Please remove comment below if debugging is needed
        // console.log('requesting embedded info from parent iframe');
        window.parent.postMessage(
            {
                iProxyRequest: true,
                internalRequest: 'embeddedInfo',
                ticket,
            },
            '*',
        );
    });
    const response = await promise;
    const json = await response.json();

    // Please remove comment below if debuggin is needed
    // console.log('returning embedded info as: ', json);

    return json;
};

const aInit = async ({ urlParams = {} }) => {
    const { insideIFrameProxy } = urlParams;

    // Please remove comment below if debugging is needed
    // console.log('initialising app');
    const promise = new Promise((resolve) => {
        try {
            if (typeof localStorage !== 'undefined') {
                if (isIframeProxy()) {
                    // We are a wrapper iframe
                    installIframeProxy();
                    resolve(null);
                } else if (
                    insideIFrameProxy ||
                    (localStorage && localStorage.insideIFrameProxy)
                ) {
                    adjustAjaxToIframeProxy();
                    aGetEmbeddedInfoFromIframeProxy().then(
                        ({ serverData, cookie }) => {
                            window.__SERVER_DATA__ = serverData || {};
                            window.embedderCookie = cookie || undefined;
                            resolve(null);
                        },
                    );
                } else {
                    resolve(null);
                }
            } else {
                resolve(null);
            }
        } catch (e) {
            //  No access to local storage, do nothing
            resolve(null);
        }
    });

    return promise;
};

export default {
    aInit,
    isIframeProxy,
};
